import React, { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import {
  Stack,
  StackItem,
  TextField,
  Toggle,
  TextStyles,
  Icon,
} from '@fluentui/react';
import useDebounce from '../../components/hooks/useDebounce';
import {
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import {
  GET_CUSTOMER_PAYMENT_TRANSACTIONS,
  ICustomerPaymentTransaction,
} from '../../utils/CustomerPaymentTransaction';
import PageHeader from '../../components/parts/PageHeader';
import CustomerPaymentTransactionsOverview from './components/CustomerPaymentTransactionOverview';

interface IPagedCustomerPaymentTransaction {
  customerPaymentTransactions: ICustomerPaymentTransaction[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  lazyLoading: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const CustomerPaymentTransactions = () => {
  const { isAuthenticated } = useAuth0();

  const [pagedState, setPagedState] =
    useState<IPagedCustomerPaymentTransaction>({
      customerPaymentTransactions: [],
      skip: 0,
      take: default_page_size,
      filter: '',
      showLoadMore: true,
      lazyLoading: true,
      isFiltered: false,
      initialLoad: true,
      isSortedAsc: true,
      sortedField: 'construction_site_name',
    });

  const [filter, setFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState(true);

  const debouncedValue = useDebounce(filter, 500);

  useEffect(() => {
    setPagedState(prevState => ({
      ...prevState,
      customerPaymentTransactions: [],
      skip: 0,
    }));
  }, [filter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    // De kolom "Werf" heet construction_site: sorteren kan enkel via de naam van de werf
    // (de server weigert { construction_site: 'asc' } en de lijst bleef dan leeg).
    if (['construction_site', 'construction_site_name'].includes(pagedState.sortedField)) {
      orderByObject.construction_site = {
        name: pagedState.isSortedAsc ? 'asc' : 'desc',
      };
    } else {
      orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
        ? 'asc'
        : 'desc';
    }

    return orderByObject;
  };

  const getFilters = (filterString: string, active: boolean) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [],
    };
    if (active) {
      filterObject.AND.push({
        customer_invoices: {
          none: {},
        },
      });
    }

    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [
          { description: { contains: filterArray[i] } },
          {
            construction_site: {
              OR: [{ name: { contains: filterArray[i] } }],
            },
          },
        ],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const {
    data: dataCustomerPaymentTransactions,
    loading,
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_CUSTOMER_PAYMENT_TRANSACTIONS, {
    // Geen client.cache.reset() meer bij zoeken/sorteren: elke filter + sortering heeft een eigen
    // cache-ingang (skipTakePagination in App.tsx). Eerst tonen wat in de cache zit en altijd
    // verversen bij de server; daarna cache-first, zodat "meer laden" geen extra verversing uitlokt.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    // notifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue, activeFilter),
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearCustomerPaymentTransactions();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const clearCustomerPaymentTransactions = () => {
    setPagedState(prevState => ({
      ...prevState,
      customerPaymentTransactions: [],
      skip: 0,
    }));
  };

  const onChangeActiveFilter = () => {
    setActiveFilter(!activeFilter);
    clearCustomerPaymentTransactions();
  };

  const fetchMoreCustomerPaymentTransactions = () => {
    // verder vanaf wat er getoond wordt (zoals bij leads/klanten)
    const currentCount = dataCustomerPaymentTransactions?.findManyCustomerPaymentTransactions?.length || 0;
    // Load more guard
    fetchMore({
      variables: {
        filter: getFilters(debouncedValue, activeFilter), // inUse == active?
        take: pagedState.take,
        skip: currentCount,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: currentCount,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>

        <PageHeader
          title='Betalingsschijven'
          iconName='PieSingle'
          loading={loading}
        />

        <Stack style={{ flexDirection: 'row' }}>
          <TextField
            label='Zoeken...'
            value={filter}
            styles={textFieldStyles300}
            onChange={onSearchValueChange}
          />

          <StackItem style={{ marginLeft: 70 }}>
            <Toggle
              label='Openstaand?'
              defaultChecked={activeFilter}
              onText='Openstaand'
              offText='Afgesloten'
              onChange={onChangeActiveFilter}
            />
          </StackItem>
        </Stack>
      </div>

      <CustomerPaymentTransactionsOverview
        refetch={refetch}
        customerPaymentTransactions={
          dataCustomerPaymentTransactions
            ? dataCustomerPaymentTransactions.findManyCustomerPaymentTransactions
            : []
        }
        loading={loading}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreCustomerPaymentTransactions}
        lazyLoading={pagedState.lazyLoading}
      />
    </Stack>
  );
};

export default CustomerPaymentTransactions;
